/**
 * Date Filter Component for Admin Control Panel
 * 
 * Provides preset date ranges and custom date picker.
 */
import React, { useState } from "react";
import { Calendar, ChevronDown, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format, subDays, startOfMonth, startOfYear } from "date-fns";

const DateFilter = ({ dateRange, onDateRangeChange, onRefresh, loading }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activePreset, setActivePreset] = useState('today');

  const presets = [
    { label: 'Today', key: 'today', getDates: () => {
      const today = new Date();
      return { start: today, end: today };
    }},
    { label: 'Last 7 Days', key: 'last7', getDates: () => {
      const end = new Date();
      const start = subDays(end, 6);
      return { start, end };
    }},
    { label: 'Last 30 Days', key: 'last30', getDates: () => {
      const end = new Date();
      const start = subDays(end, 29);
      return { start, end };
    }},
    { label: 'Last 90 Days', key: 'last90', getDates: () => {
      const end = new Date();
      const start = subDays(end, 89);
      return { start, end };
    }},
    { label: 'MTD', key: 'mtd', getDates: () => {
      const end = new Date();
      const start = startOfMonth(end);
      return { start, end };
    }},
    { label: 'YTD', key: 'ytd', getDates: () => {
      const end = new Date();
      const start = startOfYear(end);
      return { start, end };
    }},
  ];

  const handlePresetClick = (preset) => {
    const { start, end } = preset.getDates();
    setActivePreset(preset.key);
    onDateRangeChange({
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd')
    });
  };

  const handleCustomDateSelect = (range) => {
    if (range?.from && range?.to) {
      setActivePreset('custom');
      onDateRangeChange({
        start: format(range.from, 'yyyy-MM-dd'),
        end: format(range.to, 'yyyy-MM-dd')
      });
      setCalendarOpen(false);
    }
  };

  const formatDisplayDate = () => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
  };

  return (
    <div 
      className="bg-slate-900/60 border border-slate-800 rounded-xl p-4"
      data-testid="date-filter"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Preset Buttons */}
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <Button
              key={preset.key}
              variant={activePreset === preset.key ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetClick(preset)}
              className={`
                ${activePreset === preset.key 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600' 
                  : 'bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'}
              `}
              data-testid={`date-preset-${preset.key}`}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        {/* Custom Date Range Picker */}
        <div className="flex items-center gap-2 ml-auto">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`
                  ${activePreset === 'custom' 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600' 
                    : 'bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'}
                  min-w-[200px] justify-between
                `}
                data-testid="custom-date-btn"
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{formatDisplayDate()}</span>
                </div>
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="end">
              <CalendarComponent
                mode="range"
                defaultMonth={new Date(dateRange.start)}
                selected={{
                  from: new Date(dateRange.start),
                  to: new Date(dateRange.end)
                }}
                onSelect={handleCustomDateSelect}
                numberOfMonths={2}
                className="bg-slate-900 text-white"
              />
            </PopoverContent>
          </Popover>

          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DateFilter;
